import clsx from 'clsx';
import { HeaderProps } from 'react-table';

type TableHeaderColumnProps<T extends object> = {
  column: HeaderProps<T>['column'];
  title?: React.ReactNode;
  className?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  updateState?: (state: { sort?: string; order?: 'asc' | 'desc' }) => void;
};

export const TableHeaderColumn = <T extends object>({
  column,
  title,
  className,
  sort,
  order,
  updateState,
}: TableHeaderColumnProps<T>) => {
  const id = column.id;
  const isSelectedForSorting = sort === id;

  // 1. Extract key from React Table's header props to prevent key-spread warnings
  const { key: headerKey, style: headerStyle, ...restHeaderProps } = column.getHeaderProps();
  const { width: _defaultWidth, maxWidth: _defaultMaxWidth, ...cleanHeaderStyle } = headerStyle || {};

  const sortColumn = () => {
    if (!updateState || ['actions', 'selection'].includes(id) || id.match(/Attachment/i) ) {
      return;
    }

    if (!isSelectedForSorting) {
      updateState({ sort: id, order: 'asc' });
      return;
    }

    if (order === 'asc') {
      updateState({ sort: id, order: 'desc' });
      return;
    }

    updateState({ sort: undefined, order: undefined });
  };

  return (
    // 2. Render EXACTLY ONE <th> per column
    <th
      key={headerKey}
      {...restHeaderProps}
      style={{
        ...cleanHeaderStyle,        
        minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
        width: column.width && (column.width !== 150)? `${column.width}px` : undefined,
        maxWidth: column.maxWidth && (column.maxWidth < 100000) /*Number.MAX_SAFE_INTEGER */ ? `${column.maxWidth}px` : undefined,
      }}
      className={clsx(
        className,
        'cursor-pointer select-none',
        isSelectedForSorting && order !== undefined && `table-sort-${order}`
      )}
      onClick={sortColumn}
    >
      {title ?? column.render('Header')}
    </th>
  );
};